import React, { createContext, useContext, useMemo } from "react";
import i18n from "../../../i18n";
import { useAppLanguage } from "../../../utils/appLanguage";
import { getAppliedTheme } from "../../../theme/useTheme";
import { T, TONE_COLOR } from "./tokens";
import {
  AreaConfig,
  DimensionKey,
  LifeAreasConfig,
  MoodConfig,
  RingInsight,
  RingNextStep,
  TextI18n,
} from "./types";

// The Life Areas UI holds no copy of the areas, rings, bands, labels or moods:
// they come from GET /life-gps/config and are read through this context.
// The backend sends them in English; each one is shown in the user's language
// from "lifeAreas.*" (keyed by the backend ids), with the English text as the
// fallback for anything a newer backend adds.

const tr = (key: string, fallback: string, lng?: string) =>
  i18n.t(key, { defaultValue: fallback, lng });

const relatedKey = (a: string, b: string) => {
  const direct = `lifeAreas.related.${a}_${b}`;
  return i18n.exists(direct) ? direct : `lifeAreas.related.${b}_${a}`;
};

const localizeConfig = (raw: LifeAreasConfig, lng: string): LifeAreasConfig => ({
  ...raw,
  rings: raw.rings.map((r) => ({
    ...r,
    label: tr(`lifeAreas.ring.${r.id}.label`, r.label, lng),
    description: tr(`lifeAreas.ring.${r.id}.description`, r.description, lng),
  })),
  areas: raw.areas.map((a) => {
    const base = `lifeAreas.area.${a.key}`;
    return {
      ...a,
      label: tr(`${base}.label`, a.label, lng),
      description: tr(`${base}.description`, a.description, lng),
      subDimensions: a.subDimensions.map((x, i) => tr(`${base}.sub.${i}`, x, lng)),
      questions: a.questions.map((x, i) => tr(`${base}.q.${i}`, x, lng)),
      related: a.related.map((r) => ({ ...r, note: tr(relatedKey(a.key, r.key), r.note, lng) })),
    };
  }),
  bands: raw.bands.map((b) => ({ ...b, label: tr(`lifeAreas.band.${b.tone}`, b.label, lng) })),
  center: {
    ...raw.center,
    title: tr("lifeAreas.center.title", raw.center.title, lng),
    states: Object.fromEntries(
      Object.entries(raw.center.states).map(([k, v]) => [k, tr(`lifeAreas.center.${k}`, v, lng)]),
    ) as LifeAreasConfig["center"]["states"],
  },
  efforts: Object.fromEntries(
    Object.entries(raw.efforts).map(([k, v]) => [k, tr(`lifeAreas.effort.${k}`, v, lng)]),
  ) as LifeAreasConfig["efforts"],
  moods: raw.moods.map((m) => ({ ...m, label: tr(`lifeAreas.mood.${m.id}`, m.label, lng) })),
  insightTypes: Object.fromEntries(
    Object.entries(raw.insightTypes).map(([k, v]) => [
      k,
      { ...v, label: tr(`lifeAreas.insightType.${k}`, v.label, lng) },
    ]),
  ),
});

export type Band = { label: string; color: string };

export type Lookup = {
  config: LifeAreasConfig;
  areas: AreaConfig[]; // spec order
  area: (key: DimensionKey) => AreaConfig;
  // stored score (0-10) ↔ the scale the UI shows (0-100)
  display: (stored: number | null | undefined) => number | null;
  toStored: (shown: number) => number;
  bandFor: (shown: number | null) => Band | null;
  mood: (id?: string | null) => MoodConfig | undefined;
  // Backend text that has a language-neutral twin, in the user's language.
  insightText: (insight: RingInsight) => { headline: string; body: string };
  stepTitle: (step: RingNextStep) => string;
  stepReason: (step: RingNextStep) => string;
};

// One calm family: the Astria language tints, never bright per-area hues.
const SOFT_AREA_TINTS = ["#A78BFF", "#C27BFF", "#9AD7FF", "#7A7FFF", "#BBA0FF", "#FF9F8A"];

export const buildLookup = (rawConfig: LifeAreasConfig, lng = "en"): Lookup => {
  const localized = localizeConfig(rawConfig, lng);
  const config: LifeAreasConfig = {
    ...localized,
    // dark: one calm tint family; light: the per-area colors from the backend
    areas:
      getAppliedTheme() === "light"
        ? localized.areas
        : localized.areas.map((a, i) => ({
            ...a,
            color: SOFT_AREA_TINTS[i % SOFT_AREA_TINTS.length],
          })),
  };
  const byKey = new Map(config.areas.map((a) => [a.key, a]));
  const { max, storageMax } = config.scale;
  const factor = max / storageMax;
  const bandsDesc = config.bands.slice().sort((a, b) => b.min - a.min);
  // an unknown key (e.g. a newer backend) degrades to a neutral placeholder
  const area = (key: DimensionKey): AreaConfig =>
      byKey.get(key) ?? {
        key,
        label: String(key),
        emoji: "•",
        color: T.color.text.muted,
        ring: "",
        shortcut: 0,
        description: "",
        subDimensions: [],
        questions: [],
        related: [],
      };
  const bandLabel = (tone: string) =>
    config.bands.find((b) => b.tone === tone)?.label ?? tone;
  // Area keys in the params become area names before they reach the text.
  const params = (p: TextI18n["params"] = {}) => ({
    ...p,
    area: p.area ? area(p.area as DimensionKey).label : "",
    other: p.other ? area(p.other as DimensionKey).label : "",
    lng,
  });
  // Template step titles are the English catalogue titles; match them by area.
  const stepTitle = (step: RingNextStep) => {
    if (step.generatedBy !== "template") return step.title;
    for (let i = 0; i < 4; i++) {
      const key = `lifeAreas.area.${step.dimensionKey}.step.${i}`;
      if (i18n.exists(key) && i18n.t(key, { lng: "en" }) === step.title) {
        return i18n.t(key, { lng });
      }
    }
    return step.title;
  };
  return {
    config,
    areas: config.areas,
    area,
    display: (stored) => (stored == null ? null : Math.round(stored * factor)),
    toStored: (shown) => shown / factor,
    bandFor: (shown) => {
      if (shown == null || !bandsDesc.length) return null;
      const b = bandsDesc.find((x) => shown >= x.min) ?? bandsDesc[bandsDesc.length - 1];
      return { label: b.label, color: TONE_COLOR[b.tone] ?? T.color.text.secondary };
    },
    mood: (id) => (id ? config.moods.find((m) => m.id === id) : undefined),
    insightText: (insight) => {
      const k = insight.i18n?.key;
      if (!k || !i18n.exists(`lifeAreas.insight.${k}.headline`)) {
        return { headline: insight.headline, body: insight.body };
      }
      const p = params(insight.i18n?.params);
      return {
        headline: i18n.t(`lifeAreas.insight.${k}.headline`, p),
        body: i18n.t(`lifeAreas.insight.${k}.body`, p),
      };
    },
    stepTitle,
    stepReason: (step) => {
      const r = step.reasonI18n;
      if (!r?.key || !i18n.exists(`lifeAreas.reason.${r.key}`)) return step.reason;
      const band = r.params?.band ? bandLabel(String(r.params.band)) : "";
      const why = i18n.t(`lifeAreas.reason.${r.key}`, {
        ...params(r.params),
        // mid-sentence band name ("… is balanced, so …")
        band: r.key === "fair" && lng !== "fil" ? band.toLocaleLowerCase(lng) : band,
      });
      const note = r.params?.note ? String(r.params.note) : "";
      return note ? `${why} ${i18n.t("lifeAreas.reason.note", { note, lng })}` : why;
    },
  };
};

const Ctx = createContext<Lookup | null>(null);

export const LifeAreasProvider: React.FC<{ config: LifeAreasConfig; children: React.ReactNode }> = ({
  config,
  children,
}) => {
  const lng = useAppLanguage();
  const value = useMemo(() => buildLookup(config, lng), [config, lng]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useLifeAreas = (): Lookup => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLifeAreas must be used inside <LifeAreasProvider>");
  return v;
};
